import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// ── P3 new-model (4 soru tipi) wire shapes ───────────────────────────────────
// Student payload for /student/library/:bookId sections + the v2 submit body.
// The section shape is a superset of the legacy one; these fields are only
// present on new-model books (content_model: 'v2').

/** Response of studentApi.bookPages / teacherApi.bookPages. */
export interface BookPagesResponse {
  pages: { page: number; url: string | null }[];
  /** Total pages in the book, regardless of which slice came back. */
  count: number;
  /** The slice returned; null/null means the whole book. */
  page_start: number | null;
  page_end: number | null;
  node_id: number | null;
  /** 'node' = the alt başlık's own span, 'node-derived' = spanned from children. */
  scope: 'book' | 'node' | 'node-derived';
}

export type QuestionTypeV2 = 'multiple_choice' | 'fill_blank_mc' | 'true_false' | 'video_only';
export type CoverTypeV2 = 'text' | 'image' | 'audio' | 'video' | null;
export type ListingStyleV2 = 'roman' | 'letter' | 'number';

export interface QuestionOptionV2 { id: number; text: string }
export interface QuestionCaseV2 {
  id: number;
  before_text: string | null;
  after_text: string | null;
  statement_text: string | null;
  options: QuestionOptionV2[];
}
export interface SectionQuestionV2 {
  question_number: number;
  image_url: string | null;
  video_url: string | null;
  // new-model fields (absent on legacy-branch books)
  question_id?: number;
  question_type?: QuestionTypeV2;
  cover_type?: CoverTypeV2;
  cover_text?: string | null;
  cover_media_url?: string | null;
  pre_text?: string | null;
  listing_style?: ListingStyleV2;
  points?: number;
  options?: QuestionOptionV2[];
  cases?: QuestionCaseV2[];
}
export interface AnswerV2 {
  question_id: number;
  selected_option_id?: number | null;
  cases?: Array<{ case_id: number; selected_option_id?: number | null; selected_bool?: boolean | null }>;
}
// Per-question review detail written by the v2 grader (attempt answers).
export interface AnswerDetailV2 {
  selected_option_id?: number | null;
  correct_option_id?: number;
  cases?: Array<{
    case_id: number;
    selected_option_id?: number | null;
    correct_option_id?: number;
    selected_bool?: boolean | null;
    correct_bool?: boolean | null;
    is_correct: boolean;
  }>;
}

export const api = axios.create({
  baseURL: BASE_URL,
  // X-Content-Version: 2 — this build renders the new 4-type question model
  // (P3). The backend hides new-type sections from clients without it.
  headers: { 'Content-Type': 'application/json', 'X-Content-Version': '2' },
});

// Attach JWT token to every request if present
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('dijipo_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// On 401 (expired/invalid JWT), wipe the student/teacher/parent session and
// kick the browser to /login. Uses its own storage keys (dijipo_token /
// dijipo_user) so it never clobbers the admin panel's digipo_admin_* keys —
// a student who lands on the admin panel in the same browser shouldn't log
// the admin out, and vice-versa. Matches the admin panel's behavior but with
// a separate identity space.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem('dijipo_token');
      localStorage.removeItem('dijipo_user');
      // Tear the SSO marker down with the rest of the session. Left behind, it would
      // outlive the session it describes and send the NEXT logout — possibly a
      // password one — on a pointless trip through the IdP. Deliberately does NOT
      // end the IdP session itself: an expired Digipo JWT is not a signal that the
      // person wants out of every Birey product, and this fires on background 401s.
      localStorage.removeItem('dijipo_sso');
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

// Auth
export const authApi = {
  me: () => api.get<ApiUser>('/auth/me'),
  publicGrades: () => api.get<Array<{ id: number; grade_number: number | null; name: string }>>('/public/grades'),
  // AP34: okul listesi artık aranabilir + şehirle senkron (q / cityId / limit)
  publicInstitutions: (params?: { q?: string; cityId?: number; limit?: number }) =>
    api.get<Array<{ id: number; name: string }>>('/public/institutions', { params }),
  publicCities: () => api.get<Array<{ id: number; name: string }>>('/public/cities'),
  // AP35: profil fotoğrafı yükleme — multipart tek dosya, 5MB sınırı
  uploadAvatar: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{ ok: boolean; avatarUrl: string }>('/auth/me/avatar', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  updateMe: (data: { firstName?: string; lastName?: string; email?: string; phone?: string; occupation?: string | null }) =>
    api.patch('/auth/me', data),
};

// Student
export const studentApi = {
  dashboard: () => api.get('/student/dashboard'),
  assignments: (status?: string) =>
    api.get('/student/assignments', { params: { status } }),
  submitAnswers: (
    submissionId: string,
    answers: Record<number, string>,
    timeSpentSeconds: number
  ) => api.post(`/student/submissions/${submissionId}/submit`, { answers, timeSpentSeconds }),
  // Diğer Ödev submission: upload the file, then complete with its key + note.
  uploadSubmissionFile: (submissionId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{ key: string; fileName: string; fileType: string | null }>(
      `/student/submissions/${submissionId}/upload`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },
  completeOtherAssignment: (submissionId: string, data: { fileKey: string; fileName?: string; note?: string }) =>
    api.post(`/student/submissions/${submissionId}/complete`, data),
  performance: (period?: string, subjectId?: string | number, subjectUnitId?: string | number) =>
    api.get('/student/performance', { params: { period, subjectId, subjectUnitId } }),
  // AI Yorumu (Digikoç) — streamed Turkish coaching comment covering weekly +
  // monthly + all-time performance. Returns the raw fetch Response so the
  // caller can read response.body as a token stream. axios can't stream in the
  // browser, so this uses fetch directly (same base URL + bearer token).
  performanceInsightStream: (signal?: AbortSignal) =>
    fetch(`${BASE_URL}/student/performance/insight`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('dijipo_token') ?? ''}` },
      signal,
    }),
  library: () => api.get('/student/library'),
  // Lazy per-book progress (test count + accuracy). Lifted out of the library
  // grid response in Phase 3 so the home page stays responsive — call this
  // on the kitap detay page to fill in the "X/Y test tamamlandı" stat.
  bookProgress: (bookId: number | string) =>
    api.get<{
      test_count_total: number;
      test_count_completed: number;
      progress_pct: number;
      accuracy_pct: number | null;
    }>(`/student/library/${bookId}/progress`),
  // Kitap Ekle — redeem a printed book activation code.
  redeemBookCode: (code: string) =>
    api.post<{
      success: true;
      book: { id: number; title: string; cover_image_url: string | null };
      activation_id: string;
    }>('/student/library/redeem', { code }),
  bookContent: (bookId: string, unitId?: string) =>
    api.get(`/student/library/${bookId}`, { params: { unitId } }),
  // digipo per-page PDFs: page URLs in one call (the reader renders each sayfa
  // as its own document). Empty for non-digipo books.
  // Pass nodeId to get ONLY that alt başlık's printed pages — the reader mounts
  // one document per page, so asking for a 130-page book to read a 5-page lesson
  // is 130 cold S3 fetches instead of 5. The range is resolved server-side from
  // the node, so an omitted/unknown nodeId simply returns the whole book.
  bookPages: (bookId: string | number, nodeId?: number | string | null) =>
    api.get<BookPagesResponse>(`/student/library/${bookId}/pages`, {
      params: nodeId != null ? { nodeId } : undefined,
    }),
  submitSectionAttempt: (
    bookId: string | number,
    sectionLabel: string,
    answers: Record<number, string | null>,
    timeSpentSeconds?: number,
    submissionId?: string,
    // P3 v2 payload — new-model tests are graded server-side from
    // test_questions; when testId is set, `answers` is ignored by the backend.
    v2?: { testId: number; answersV2: AnswerV2[] },
  ) =>
    api.post(
      `/student/library/${bookId}/sections/${encodeURIComponent(sectionLabel)}/attempt`,
      { answers, timeSpentSeconds, submissionId, testId: v2?.testId, answersV2: v2?.answersV2 }
    ),
  schedule: () => api.get('/student/schedule'),
  goals: (scope?: 'active' | 'archived' | 'all') =>
    api.get('/student/goals', { params: scope ? { scope } : undefined }),
  createGoal: (data: {
    title?: string;
    subjectId?: string | number;
    targetCount: number;
    // 'daily' | 'weekly' | 'monthly' — window the solved-question count spans.
    periodType?: 'daily' | 'weekly' | 'monthly';
    date?: string;
    startTime?: string;
    endTime?: string;
  }) => api.post('/student/goals', data),
  updateGoal: (goalId: string, currentCount: number, correctCount?: number, wrongCount?: number) =>
    api.patch(`/student/goals/${goalId}`, { currentCount, correctCount, wrongCount }),
  deleteGoal: (goalId: string) => api.delete(`/student/goals/${goalId}`),
  // T9 — dışarıda çözülen soruların manuel kaydı. Auto-tracked hedeflerde
  // platform sayılarının üstüne eklenir (goal_practice_entries).
  addGoalPractice: (goalId: string, data: { date?: string; solved: number; correct?: number; wrong?: number }) =>
    api.post(`/student/goals/${goalId}/practice`, data),
  deleteGoalPractice: (goalId: string, entryId: string) =>
    api.delete(`/student/goals/${goalId}/practice/${entryId}`),
  deleteScheduleEntry: (entryId: string) => api.delete(`/student/schedule/entries/${entryId}`),
  updateScheduleEntry: (entryId: string, data: {
    dayOfWeek?: number; startTime?: string; endTime?: string;
    subjectId?: number; title?: string; targetQuestions?: number;
  }) => api.patch(`/student/schedule/entries/${entryId}`, data),
  // Günlük Verilerim — student-only personal study log. Was localStorage;
  // moved to the student_daily_entries table. Entries are intentionally
  // not joined into performance rollups (the page itself says so).
  listDailyEntries: () => api.get<Array<{
    id: string;
    entry_date: string;
    study_minutes: number;
    questions_solved: number;
    mood: string;
    notes: string;
    created_at: string;
  }>>('/student/daily-entries'),
  createDailyEntry: (data: { entryDate: string; studyMinutes: number; questionsSolved: number; mood?: string; notes?: string }) =>
    api.post<{ id: string }>('/student/daily-entries', data),
  deleteDailyEntry: (entryId: string) =>
    api.delete(`/student/daily-entries/${entryId}`),
  digikoc: () => api.get<{
    podcasts: Array<{ id: string; kind: 'podcast'; title: string; thumbnail_url: string | null; duration: string | null; duration_seconds: number | null; video_url: string | null; created_at: string }>;
    videos:   Array<{ id: string; kind: 'sinava_dogru'; title: string; thumbnail_url: string | null; duration: string | null; duration_seconds: number | null; video_url: string | null; created_at: string }>;
  }>('/student/digikoc'),
  // Digikoç Koçluk — per-student personalized coaching panel (weak/strong
  // kazanımlar + remedial content per weak kazanım).
  digikocCoach: () => api.get<{
    student_name: string;
    student_grade: number | null;
    has_data: boolean;
    // Digikoç climbs canonical MEB kazanımı → alt bölüm → bölüm → kitap.
    // PLE subject_units never satisfy the kazanım rung; `key` is stable.
    weak: Array<{
      key: string;
      level: 'kazanim' | 'alt_bolum' | 'bolum' | 'kitap';
      level_label: string;
      // Set when the kazanım was borrowed from a parent scope at read time
      // because the question carries none of its own; null when it's the
      // question's own. Nothing is ever written onto the question row.
      inherited_from: 'alt_bolum' | 'bolum' | 'kitap' | null;
      code: string | null;
      learning_outcome_id: number | null;
      node_id: number | null;
      title: string;
      subject_id: number | null;
      subject_name: string | null;
      book_title: string | null;
      correct_count: number;
      wrong_count: number;
      total_count: number;
      accuracy_pct: number;
      mode: 'solution_videos' | 'lecture_guide';
      // Solution videos from books the student OWNS — playable now.
      videos: Array<{ book_id: string; book_title: string; section_label: string; question_number: number; video_url: string | null; thumbnail_url: string | null }>;
      // In-grade books she does NOT own that carry solution videos for this
      // bucket — locked upsell cards (buy the book to unlock).
      previews: Array<{ book_id: string; book_title: string; locked_count: number; cover_url: string | null; catalog_pdf_url: string | null; publisher_name: string | null; publisher_website: string | null }>;
      guide: { headline: string; message: string } | null;
    }>;
    strong: Array<{
      key: string;
      level: 'kazanim' | 'alt_bolum' | 'bolum' | 'kitap';
      level_label: string;
      inherited_from: 'alt_bolum' | 'bolum' | 'kitap' | null;
      code: string | null;
      learning_outcome_id: number | null;
      node_id: number | null;
      title: string;
      subject_id: number | null;
      subject_name: string | null;
      book_title: string | null;
      correct_count: number;
      wrong_count: number;
      total_count: number;
      accuracy_pct: number;
      // Strong topics are now enriched exactly like weak ones (videos +
      // performance) so "Güçlü Konular" can show çözümlü videos too. `previews`
      // stays empty for strong (no upsell), `guide` is null (mode is always
      // 'solution_videos' at >=80% accuracy).
      mode: 'solution_videos' | 'lecture_guide';
      videos: Array<{ book_id: string; book_title: string; section_label: string; question_number: number; video_url: string | null; thumbnail_url: string | null }>;
      previews: Array<{ book_id: string; book_title: string; locked_count: number; cover_url: string | null; catalog_pdf_url: string | null; publisher_name: string | null; publisher_website: string | null }>;
      guide: { headline: string; message: string } | null;
    }>;
  }>('/student/digikoc/personalized'),
  onlineExams: () => api.get('/student/online-exams'),
  classes: () => api.get('/student/classes'),
  joinClass: (joinCode: string) => api.post('/student/classes/join', { joinCode }),
  // Leaving is now a request the teacher must approve (see teacherApi below).
  requestLeaveClass: (classId: string | number) => api.post(`/student/classes/${classId}/leave-request`),
  cancelLeaveRequest: (classId: string | number) => api.delete(`/student/classes/${classId}/leave-request`),
  subjects: () => api.get('/student/subjects'),
  institutions: () => api.get('/student/institutions'),
  medals: () => api.get('/student/medals'),
  setMedalShowcase: (achievementId: string | number, isShowcased: boolean) =>
    api.patch(`/student/medals/${achievementId}/showcase`, { isShowcased }),
  appointments: () => api.get('/student/appointments'),
  // from/to: YYYY-MM-DD (TR günü) hafta/ay penceresi; my_bookings pencereden
  // bağımsız tüm gelecek rezervasyonlar
  librarySlots: (from?: string, to?: string) => api.get<{
    balance: number;
    my_bookings: Array<{
      id: string;
      institution_name: string;
      start_at: string;
      end_at: string;
      capacity: number;
      reserved_count: number;
      credit_cost: number;
      assigned_teacher_name?: string | null;
      my_status: 'booked' | null;
    }>;
    slots: Array<{
      id: string;
      institution_name: string;
      start_at: string;
      end_at: string;
      capacity: number;
      reserved_count: number;
      credit_cost: number;
      assigned_teacher_name?: string | null;
      my_status: 'booked' | null;
    }>;
  }>('/student/library-slots', { params: { ...(from ? { from } : {}), ...(to ? { to } : {}) } }),
  bookLibrarySlot: (slotId: string) =>
    api.post<{ success: true; booking_id: string; new_balance: number }>(`/student/library-slots/${slotId}/book`),
  cancelLibraryBooking: (slotId: string) =>
    api.delete<{ success: true; new_balance: number }>(`/student/library-slots/${slotId}/book`),
  // Teachers the student can request an appointment with (their own classes).
  appointmentTeachers: () =>
    api.get<Array<{ id: string; uuid: string; firstName: string; lastName: string; subjects: { id: number; name: string }[] }>>('/student/appointment-teachers'),
  createAppointment: (data: {
    teacherUserId: string;
    subjectId?: number;
    appointmentDate: string;      // ISO date (day)
    startTime?: string;           // "HH:MM"
    endTime?: string;             // "HH:MM"
    format?: 'face_to_face' | 'online' | 'phone';
    notes?: string;
  }) => api.post('/student/appointments', data),
  // B7: teacher's busy time ranges on a day (so the student can pick a free slot).
  teacherAvailability: (teacherUserId: string, date: string) =>
    api.get<{ date: string; busy: { start_time: string; end_time: string }[] }>(
      `/student/teacher-availability`, { params: { teacherUserId, date } }),
  smartboard: () => api.get('/student/smartboard'),
  saveSmartboard: (data: { title: string; canvasData: any; thumbnailUrl?: string; saveId?: string }) =>
    api.post('/student/smartboard', data),
  saveDraft: (bookId: number | string, sectionLabel: string, answers: Record<number, string>, timeSpentSeconds: number) =>
    api.put(`/student/draft/${bookId}/${encodeURIComponent(sectionLabel)}`, { answers, timeSpentSeconds }),
  loadDraft: (bookId: number | string, sectionLabel: string) =>
    api.get(`/student/draft/${bookId}/${encodeURIComponent(sectionLabel)}`),
  saveAnnotations: (bookId: number | string, sectionKey: string, data: any) =>
    api.put(`/student/annotations/${bookId}/${encodeURIComponent(sectionKey)}`, data),
  loadAnnotations: (bookId: number | string, sectionKey: string) =>
    api.get(`/student/annotations/${bookId}/${encodeURIComponent(sectionKey)}`),
  credits: () => api.get<{ balance: number }>('/student/credits'),
  // Deneme sınavı randevuları
  examAppointments: () => api.get('/student/exam-appointments'),
  registerExamAppointment: (id: string) => api.post(`/student/exam-appointments/${id}/register`),
  cancelExamRegistration: (id: string) => api.delete(`/student/exam-appointments/${id}/register`),
};

// Teacher
export const teacherApi = {
  dashboard: () => api.get('/teacher/dashboard'),
  classes: () => api.get('/teacher/classes'),
  // Join a class as a teacher via a class join code (mirror of student joinClass).
  joinClass: (joinCode: string) =>
    api.post<{ ok: true; class: { id: string; name: string }; subjects_assigned: number }>('/teacher/classes/join', { joinCode }),
  // AP39: add a student to one of the teacher's classes by the student's
  // personal code (student_profiles.student_code).
  addStudentToClass: (classId: string | number, studentCode: string) =>
    api.post<{ ok: true; class: { id: string; name: string }; student: { uuid: string | null; name: string } }>(
      `/teacher/classes/${classId}/students`, { studentCode }
    ),
  // Resolve a student's request to leave a class (enrollment id from /teacher/classes).
  approveLeaveRequest: (enrollmentId: string | number) => api.post(`/teacher/enrollments/${enrollmentId}/approve-leave`),
  denyLeaveRequest: (enrollmentId: string | number) => api.post(`/teacher/enrollments/${enrollmentId}/deny-leave`),
  // Teacher removes a student from a class directly (withdraws the enrollment).
  removeStudentFromClass: (enrollmentId: string | number) => api.post(`/teacher/enrollments/${enrollmentId}/remove`),
  // Kuruma ait kütüphane saatlerinden bu öğretmene atananlar (T29).
  // from/to: YYYY-MM-DD (TR günü) Gün/Hafta/Ay penceresi
  libraryDuties: (from?: string, to?: string) => api.get<Array<{
    id: string;
    institution_name: string;
    start_at: string;
    end_at: string;
    capacity: number;
    reserved_count: number;
    students: Array<{ uuid: string | null; name: string; status: 'booked' | 'attended' }>;
  }>>('/teacher/library-duties', { params: { ...(from ? { from } : {}), ...(to ? { to } : {}) } }),
  assignments: (archived?: boolean) => api.get('/teacher/assignments', { params: { archived } }),
  createAssignment: (data: any) => api.post('/teacher/assignments', data),
  // Edit an existing assignment (title + son tarih). Creator-only server-side.
  updateAssignment: (id: string | number, data: { title?: string; dueDate?: string | null; indefinite?: boolean }) =>
    api.patch(`/teacher/assignments/${id}`, data),
  // D6: MEB kazanımı + kitap konusu option lists and attach editing.
  assignmentOutcomeOptions: (params: { classId?: string | number; bookId?: number; sectionLabel?: string; testId?: number; assignmentId?: string | number }) =>
    api.get('/teacher/assignment-outcome-options', { params }),
  setAssignmentOutcomes: (id: string | number, data: { mebIds: number[]; bookUnitIds: number[] }) =>
    api.put(`/teacher/assignments/${id}/learning-outcomes`, data),
  closeAssignment: (id: string | number) => api.delete(`/teacher/assignments/${id}`),
  // B22: toggle student visibility (reversible). visible=false hides from students.
  setAssignmentVisibility: (id: string | number, visible: boolean) =>
    api.patch(`/teacher/assignments/${id}/visibility`, { visible }),
  releaseAnswers: (id: string | number) => api.patch(`/teacher/assignments/${id}/release-answers`),
  assignmentSubmissions: (id: string | number) => api.get(`/teacher/assignments/${id}/submissions`),
  gradeSubmission: (submissionId: string | number, data: { score: number; feedback?: string }) =>
    api.patch(`/teacher/submissions/${submissionId}/grade`, data),
  library: () => api.get('/teacher/library'),
  redeemBookCode: (code: string) =>
    api.post<{ success: true; book: { id: number; title: string; cover_image_url: string | null }; activation_id: string }>(
      '/teacher/library/redeem', { code }
    ),
  redeemInstitutionCode: (code: string) =>
    api.post<{
      success: true;
      institution: { id: number; name: string };
      package: { id: string; name: string; max_students: number; max_classes: number; max_credits: number } | null;
    }>('/teacher/institution-codes/redeem', { code }),
  myInstitutionCodes: () =>
    api.get<Array<{
      id: string;
      redeemed_at: string;
      code: string;
      institution: { id: number; name: string } | null;
      package: {
        id: string; name: string;
        max_students: number; max_classes: number; max_credits: number;
        used_students: number; used_classes: number; used_credits: number;
        status: string;
      } | null;
    }>>('/teacher/institution-codes'),
  performance: (classId?: string | number, period?: string, subjectId?: number | string) => api.get('/teacher/performance', { params: { classId, period, subjectId } }),
  // B23 — Öğrenci Performansı: completion + accuracy over the teacher's assigned
  // homeworks. All filters optional; omit all for general performance.
  assignmentPerformance: (params?: { classId?: string; subjectId?: string; studentUuid?: string }) =>
    api.get('/teacher/assignment-performance', { params }),
  schedule: () => api.get('/teacher/schedule'),
  appointments: () => api.get('/teacher/appointments'),
  updateAppointment: (id: string | number, status: string) =>
    api.patch(`/teacher/appointments/${id}`, { status }),
  setAppointmentNotes: (id: string | number, notes: string) =>
    api.patch(`/teacher/appointments/${id}`, { notes }),
  smartBoard: () => api.get('/teacher/smart-board'),
  institutions: () => api.get('/teacher/institutions'),
  digikoc: () => api.get<{
    podcasts: Array<{ id: string; kind: 'podcast'; title: string; thumbnail_url: string | null; duration: string | null; duration_seconds: number | null; video_url: string | null; created_at: string }>;
    videos:   Array<{ id: string; kind: 'sinava_dogru'; title: string; thumbnail_url: string | null; duration: string | null; duration_seconds: number | null; video_url: string | null; created_at: string }>;
  }>('/teacher/digikoc'),
  onlineExams: () => api.get('/teacher/online-exams'),
  classesForAssignment: () => api.get('/teacher/classes-for-assignment'),
  booksForAssignment: (classId?: string) => api.get('/teacher/books-for-assignment', { params: classId ? { classId } : {} }),
  createAppointment: (data: any) => api.post('/teacher/appointments', data),
  studentsForAppointment: () => api.get('/teacher/students-for-appointment'),
  updateProfile: (data: any) => api.put('/teacher/profile', data),
  notifications: () => api.get('/teacher/notifications'),
  markAllNotificationsRead: () => api.patch('/teacher/notifications/read-all'),
  goalDefinitions: () => api.get('/teacher/goal-definitions'),
  // Konular (subject_units) for the Hedef Ata topic picker.
  kazanims: (subjectId: number, q?: string) =>
    api.get<Array<{ id: number; title: string; level: number; parent_unit_id: number | null }>>(
      '/teacher/kazanims', { params: { subjectId, q } }
    ),
  // Phase 7: optional `homework` arg creates an Assignment alongside the goal
  // and back-links student_goals.assignment_id so the student's Programım
  // card can light up "Ödeve Git" / "Kitaba Git". Phase 10: optional
  // subjectUnitId tags the goal with a source-backed konu.
  // AP69: subjectId — öğretmenin seçtiği ders; backend tanımın ders-bazlı
  // varyantına bağlar ki hedef kartlarında ders adı/ikonu görünsün.
  assignGoalToStudent: (studentUserId: string, goalDefinitionId: number, targetValue: number, goalDate?: string, startTime?: string, endTime?: string, homework?: { title?: string; description?: string; bookId?: number; sectionLabel?: string; dueDate?: string }, subjectUnitId?: number, subjectId?: number) =>
    api.post('/teacher/goals/assign-student', { studentUserId, goalDefinitionId, targetValue, goalDate, startTime, endTime, homework, subjectUnitId, subjectId }),
  assignGoalToClass: (classId: string, goalDefinitionId: number, targetValue: number, goalDate?: string, startTime?: string, endTime?: string, homework?: { title?: string; description?: string; bookId?: number; sectionLabel?: string; dueDate?: string }, subjectUnitId?: number, subjectId?: number) =>
    api.post(`/teacher/goals/assign-class/${classId}`, { goalDefinitionId, targetValue, goalDate, startTime, endTime, homework, subjectUnitId, subjectId }),
  studentPerformance: (studentUuid: string, period?: string, subjectId?: string) =>
    api.get(`/teacher/student-performance/${studentUuid}`, { params: { period, subjectId } }),
  bookSections: (bookId: number | string) => api.get(`/teacher/book/${bookId}/sections`),
  // Full Kitap İçeriği payload — same shape as studentApi.bookContent so the
  // shared BookDetailView renders the teacher's read-only view identically.
  bookContent: (bookId: number | string) => api.get(`/teacher/book/${bookId}/content`),
  // Teacher twin of studentApi.bookPages — same nodeId narrowing.
  bookPages: (bookId: number | string, nodeId?: number | string | null) =>
    api.get<BookPagesResponse>(`/teacher/book/${bookId}/pages`, {
      params: nodeId != null ? { nodeId } : undefined,
    }),
  studentProgram: (studentUuid: string) =>
    api.get(`/teacher/student-program/${studentUuid}`),
  deleteStudentScheduleEntry: (studentUuid: string, entryId: string | number) =>
    api.delete(`/teacher/student-program/${studentUuid}/entries/${entryId}`),
  deleteStudentGoal: (studentUuid: string, goalId: string | number) =>
    api.delete(`/teacher/student-program/${studentUuid}/goals/${goalId}`),
  // Phase 8 — only teachers can toggle the green-check completion flag on a
  // student's plan/goals. Students + parents render the resulting state.
  setScheduleEntryCompletion: (studentUuid: string, entryId: string | number, isCompleted: boolean) =>
    api.patch(`/teacher/student-program/${studentUuid}/entries/${entryId}/complete`, { isCompleted }),
  setGoalCompletion: (studentUuid: string, goalId: string | number, isCompleted: boolean) =>
    api.patch(`/teacher/student-program/${studentUuid}/goals/${goalId}/complete`, { isCompleted }),
  // Diğer Ödev file upload — returns { uploadUrl, key } for direct S3 PUT
  getUploadUrl: (fileName: string, contentType?: string) =>
    api.post<{ uploadUrl: string; key: string }>('/teacher/upload-url', { fileName, contentType }),
  // Preferred Diğer Ödev upload: stream the file through the backend to S3 and
  // get back the stored { key }. Avoids the browser→S3 CORS dependency the
  // presigned-PUT path (getUploadUrl) has.
  uploadHomeworkFile: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{ key: string; filename: string }>('/teacher/assignments/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // T-1: teacher PDF annotations. Shape mirrors studentApi.saveAnnotations /
  // loadAnnotations so the same SmartboardCanvas props work on either side.
  saveAnnotations: (bookId: number | string, sectionKey: string, data: any) =>
    api.put(`/teacher/annotations/${bookId}/${encodeURIComponent(sectionKey)}`, data),
  loadAnnotations: (bookId: number | string, sectionKey: string) =>
    api.get(`/teacher/annotations/${bookId}/${encodeURIComponent(sectionKey)}`),
  // Class creation + per-class join code management. Code rows match the
  // admin panel's ClassJoinCode shape (see DigipoPanel/services/api.ts).
  createClass: (data: { name: string; gradeLevelId: number; capacity?: number; institutionId?: number }) =>
    api.post('/teacher/classes', data),
  classCodes: (classId: string | number) => api.get(`/teacher/classes/${classId}/codes`),
  createClassCode: (classId: string | number, data: { expiresAt?: string | null; maxUses?: number | null; note?: string | null; bookIds?: number[] }) =>
    api.post(`/teacher/classes/${classId}/codes`, data),
  // Quota — class_count/limit + student_count/limit + active_code_count/limit.
  quota: () => api.get<{
    class_count: number; class_limit: number;
    student_count: number; student_limit: number;
    active_code_count: number; active_code_limit: number;
  }>('/teacher/quota'),
  updateClassCode: (codeId: string | number, data: { isActive?: boolean; expiresAt?: string | null; maxUses?: number | null; note?: string | null }) =>
    api.patch(`/teacher/classes/codes/${codeId}`, data),
  revokeClassCode: (codeId: string | number) => api.delete(`/teacher/classes/codes/${codeId}`),
  // ── Toplu Hedef Atama (.xlsx) — bulk study-plan upload ──
  // Downloads the template as a blob (endpoint needs the bearer token, so we
  // can't just window.open it).
  bulkGoalTemplate: () => api.get('/teacher/goals/bulk-template', { responseType: 'blob' }),
  // apply=false → dry-run PREVIEW (per-student counts + conflicts, no writes);
  // apply=true → commit. `file` is appended LAST (backend reads fields first).
  bulkAssignGoalsUpload: (
    target: { targetType: 'class'; classId: string | number } | { targetType: 'student'; studentUserId: string | number },
    file: File,
    opts: { apply: boolean; replaceExisting?: boolean },
    onProgress?: (pct: number) => void,
  ) => {
    const fd = new FormData();
    fd.append('targetType', target.targetType);
    if (target.targetType === 'class') fd.append('classId', String(target.classId));
    else fd.append('studentUserId', String(target.studentUserId));
    fd.append('apply', opts.apply ? '1' : '0');
    if (opts.replaceExisting) fd.append('replaceExisting', '1');
    fd.append('file', file, file.name || 'calisma-plani.xlsx');
    return api.post<BulkGoalUploadResult>('/teacher/goals/bulk-upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => { if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100)); },
      timeout: 0, maxContentLength: Infinity, maxBodyLength: Infinity,
    });
  },
};

export interface BulkGoalUploadResult {
  ok: boolean;
  dryRun: boolean;
  created: number;
  skipped: number;
  totalRows: number;
  studentCount: number;
  conflicts: { student: string; row: number; message: string }[];
  perStudent: { studentId: string; name: string; create: number; skip: number }[];
  replacedExisting: number;
  capped: boolean;
  warnings: string[];
}

// Parent
export const parentApi = {
  dashboard: () => api.get('/parent/dashboard'),
  children: () => api.get('/parent/children'),
  childPerformance: (childId: string | number, period?: string, subjectId?: string | number, subjectUnitId?: string | number) =>
    api.get(`/parent/children/${childId}/performance`, { params: { period, subjectId, subjectUnitId } }),
  // Phase 15 — parent reads the child's schedule + goals (read-only,
  // mirrors teacher's /teacher/student-program/:uuid).
  childProgram: (childId: string | number) =>
    api.get(`/parent/children/${childId}/program`),
  childAssignments: (childId: string | number, status?: string) =>
    api.get(`/parent/children/${childId}/assignments`, { params: { status } }),
  addChild: (studentCode: string) =>
    api.post('/parent/children', { studentCode }),
  appointments: () => api.get('/parent/appointments'),
  createAppointment: (data: any) => api.post('/parent/appointments', data),
  respondAppointment: (id: string | number, action: 'approve' | 'decline') =>
    api.patch(`/parent/appointments/${id}`, { action }),
  teachersForChild: (childId: string | number) =>
    api.get<Array<{ id: string; uuid: string; firstName: string; lastName: string; subjects: { id: number; name: string }[] }>>(`/parent/children/${childId}/teachers`),
  // B7: teacher's busy time ranges on a day (so the parent can pick a free slot).
  teacherAvailability: (teacherUserId: string, date: string) =>
    api.get<{ date: string; busy: { start_time: string; end_time: string }[] }>(
      `/parent/teacher-availability`, { params: { teacherUserId, date } }),
  // Çocuğun deneme sınavı kayıtları
  childExamRegistrations: (childId: string | number) =>
    api.get(`/parent/children/${childId}/exam-registrations`),
};

// Exam countdown (public) — drives LGS/YKS sayaç on the dashboards.
export const examsApi = {
  countdown: () =>
    api.get<{
      LGS: { date: string; daysLeft: number };
      YKS: { firstSession: string; secondSession: string; daysLeft: number };
    }>('/exams/countdown'),
};

// Public "Deneme Sınavı Videoları" — anonymous, no-token catalog + video viewer
// for exam books. Reachable from the Login screen; the shared `api` instance
// omits the Authorization header when there is no token, and the backend routes
// never 401, so these work for logged-out visitors.
export interface DenemeBook {
  id: string;
  title: string;
  display_name: string;
  cover_url: string | null;
  digipo_book_type: string | null;
  subject: { id: number; name: string } | null;
  grade_levels: { id: number; grade_number: number; name: string } | null;
  publishers: { name: string } | null;
}
export interface DenemeVideo {
  id: string;
  title: string;
  short_title: string | null;
  url: string | null;
}
export interface DenemeSection {
  section_label: string;
  section_display_label?: string | null;
  section_topic: string | null;
  section_category: string;
  lesson_video_url: string | null;
  videos: DenemeVideo[];
}
export interface DenemeBookDetail extends DenemeBook {
  sections: DenemeSection[];
}
export const publicApi = {
  denemeBooks: () => api.get<DenemeBook[]>('/public/deneme-books'),
  denemeBookContent: (id: string | number) =>
    api.get<DenemeBookDetail>(`/public/deneme-books/${id}`),
};

// Notifications
export const notificationsApi = {
  list: () => api.get('/notifications'),
  markRead: (id: string) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.patch('/notifications/read-all'),
};

// Stories — shared across all roles (platform-wide feed).
export const storiesApi = {
  list: () => api.get<Story[]>('/stories'),
};

export interface Story {
  id: string;
  media_type: 'image' | 'video';
  media_url: string;
  thumbnail_url: string | null;
  caption: string | null;
  link_url: string | null;
  link_label: string | null;
  duration_seconds: number;
  published_at: string;
}

export interface ApiUser {
  id: string;
  name: string;
  surname: string;
  email: string;
  role: 'student' | 'teacher' | 'parent';
  studentCode?: string | null;
  studentNumber?: string | null;
  avatarUrl?: string | null;
  phone?: string | null;
  dateOfBirth?: string | null;
  gender?: 'male' | 'female' | 'other' | null;
  enrollmentDate?: string | null;
  currentStreak?: number;
  longestStreak?: number;
  institution?: { id: number; name: string } | null;
  institutions?: { id: number; name: string }[];
  grade?: { id: number; name: string } | null;
  className?: string | null;
}

// ── Hukuki metinler (KVKK aydınlatma metni, çerez politikası, çerez listesi) ──
// Metin sunucudan gelir, istemcide sabit tutulmaz: aynı metni CRA, yönetim
// paneli ve mobil uygulama okuyor ve mobilde OTA güncelleme kapalı
// (expo.modules.updates.ENABLED=false). Sunucudan servis edilince bir metin
// revizyonu kurulu mobil uygulamaya mağaza sürümü olmadan ulaşıyor.

export type LegalBlock =
  | { type: 'heading'; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'links'; items: { label: string; url: string }[] }
  | { type: 'table'; columns: string[]; rows: string[][] };

export type ConsentCategoryId = 'zorunlu' | 'islev' | 'analiz' | 'hedefleme';

export interface ConsentCategory {
  id: ConsentCategoryId;
  label: string;
  description: string;
  required: boolean;
  /** Uygulamanın bugün bu kategoride fiilen kullandığı bir kayıt var mı. */
  in_use: boolean;
}

/** 'lms' = uygulamanın kendi metinleri, 'kurumsal' = BRY Birey / eis ekosistem. */
export type LegalScope = 'lms' | 'kurumsal';

export interface LegalDocumentSummary {
  slug: string;
  title: string;
  short_title: string;
  scope: LegalScope;
  version: string;
  effective_date: string;
}

export interface LegalDocument {
  slug: string;
  title: string;
  short_title: string;
  scope: LegalScope;
  version: string;
  effective_date: string;
  body: LegalBlock[];
  consent?: { version: string; categories: ConsentCategory[] };
}

export const legalApi = {
  list: () => api.get<{ documents: LegalDocumentSummary[] }>('/public/legal'),
  document: (slug: string) => api.get<LegalDocument>(`/public/legal/${slug}`),
  cookieConsentConfig: () =>
    api.get<{ version: string; categories: ConsentCategory[] }>('/public/cookie-consent'),
  /** Giriş yapmış kullanıcının çerez tercihini sunucu tarafında kayda geçirir. */
  recordConsent: (body: {
    consent_version: string;
    categories: ConsentCategoryId[];
    source?: 'banner' | 'preferences';
  }) => api.post<{ ok: boolean }>('/legal/consent', body),
};
